import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faDatabase } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';
import { getUserId } from '../../utils/userUtils';
import './DataExport.css';

/**
 * DataExport component for admin data export functionality
 * Allows Super Admins to run custom queries and export data for any account.
 * Historically ADARA-only; the account selector defaults to ADARA so the
 * legacy workflow is unchanged.
 */
// Build YYYY-MM-DD strings for the date inputs. Default range is the
// last 3 months so a typical export stays under the GCP LB timeout —
// the operator can widen or shorten it via the date pickers.
const todayYmd = () => new Date().toISOString().slice(0, 10);
const threeMonthsAgoYmd = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().slice(0, 10);
};

const DataExport = ({ userId, refreshTrigger, onRefresh }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [error, setError] = useState(null);
  // Account selector — populated from /api/admin/accounts (role-filtered
  // server-side). Defaults to ADARA when present to match the legacy
  // ADARA-only export behavior.
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountsLoading, setAccountsLoading] = useState(true);
  // Date filter — defaults to last 3 months. "All" toggles full-history.
  const [startDate, setStartDate] = useState(threeMonthsAgoYmd());
  const [endDate, setEndDate] = useState(todayYmd());
  const [fullHistory, setFullHistory] = useState(false);
  // Off by default — the raw platform result JSON dominates the file size.
  // The per-campaign metric counts are included either way.
  const [includeFullResults, setIncludeFullResults] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      setAccountsLoading(true);
      const response = await api.get(getApiUrl('/api/admin/accounts'), {
        params: { userId: getUserId() }
      });
      const fetched = Array.isArray(response.data) ? response.data : [];
      setAccounts(fetched);
      // Default to ADARA (legacy behavior), else the first account.
      setSelectedAccountId((current) => {
        if (current && fetched.some((a) => a.id === current)) {
          return current;
        }
        const adara = fetched.find((a) => a.name === 'ADARA');
        return adara ? adara.id : (fetched[0] ? fetched[0].id : '');
      });
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load accounts');
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts, refreshTrigger]);

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId) || null;
  const selectedAccountName = selectedAccount ? selectedAccount.name : '';

  const handleDataExport = async () => {
    try {
      setIsExporting(true);
      setError(null);
      const rangeLabel = fullHistory ? 'full history' : `${startDate} → ${endDate}`;
      setExportStatus(`Executing query and processing data for ${selectedAccountName} (${rangeLabel})...`);

      console.log(`Starting data export for account ${selectedAccountName}...`);

      const response = await api.post(getApiUrl('/api/admin/adara-data-export'), {
        userId: getUserId(),
        accountId: selectedAccountId,
        // Backend treats startDate/endDate=='all' as full-history.
        startDate: fullHistory ? 'all' : startDate,
        endDate: fullHistory ? 'all' : endDate,
        includeFullResults,
      });

      if (response.data) {
        // Create blob and download file
        const jsonString = JSON.stringify(response.data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        // Generate filename with account slug + timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const accountSlug = (selectedAccountName || 'account')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '') || 'account';
        const filename = `${accountSlug}-data-export-${timestamp}.json`;

        // Create download link and trigger download
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        const t = response.data.totals;
        const totalsLabel = t
          ? ` Totals — audiences: ${t.audiences}, geos: ${t.geos}, private deals: ${t.private_deals}, keywords: ${t.keywords}, creatives: ${t.creatives}.`
          : '';
        setExportStatus(`Export completed successfully! ${response.data.total_records} records exported.${totalsLabel}`);
        console.log(`Data export for ${selectedAccountName} completed successfully`);
      }
    } catch (error) {
      console.error('Error exporting account data:', error);
      setError(error.response?.data?.details || error.message || 'Failed to export data');
      setExportStatus(null);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="data-export-container">
      <div className="data-export-header">
        <h2>Data Export</h2>
        <p>Export data from the database with custom processing</p>
      </div>

      <div className="export-section">
        <div className="export-card">
          <div className="export-card-header">
            <FontAwesomeIcon icon={faDatabase} className="export-icon" />
            <h3>Account Data Export</h3>
          </div>

          <div className="export-card-content">
            <p>
              Export account messages with PDF file metadata parsing and campaign processor results.
              This will:
            </p>
            <ul>
              <li>Query messages from the selected account's users</li>
              <li>Parse PDF files from metadata</li>
              <li>Retrieve campaign-processor results</li>
              <li>Extract campaign URLs and advertiser information</li>
              <li>Count audiences, geos, private deals (PMP), keywords and creatives per campaign</li>
              <li>Download as a comprehensive JSON file</li>
            </ul>
          </div>

          {/* Account selector — role-filtered server-side; defaults to ADARA. */}
          <div className="export-account-select">
            <label className="export-date-label">
              <span>Account</span>
              <select
                className="export-account-dropdown"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                disabled={isExporting || accountsLoading}
              >
                {accountsLoading && <option value="">Loading accounts...</option>}
                {!accountsLoading && accounts.length === 0 && (
                  <option value="">No accounts available</option>
                )}
                {!accountsLoading && accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Date range filter — defaults to last 3 months so typical
              exports stay under the GCP LB 5-min timeout. */}
          <div className="export-date-range">
            <div className="export-date-controls">
              <label className="export-date-label">
                <span>Start date</span>
                <input
                  type="date"
                  className="export-date-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isExporting || fullHistory}
                />
              </label>
              <label className="export-date-label">
                <span>End date</span>
                <input
                  type="date"
                  className="export-date-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isExporting || fullHistory}
                />
              </label>
              <label className="export-full-history-toggle">
                <input
                  type="checkbox"
                  checked={fullHistory}
                  onChange={(e) => setFullHistory(e.target.checked)}
                  disabled={isExporting}
                />
                <span>Full history (no date filter)</span>
              </label>
            </div>
            <small className="export-date-help">
              Default: last 3 months. Wider ranges can exceed the export timeout.
            </small>
          </div>

          {/* Detail level. Per-campaign counts (audiences, geos, PMP deals,
              keywords, creatives) are always included; this adds the raw
              platform result JSON on top. */}
          <div className="export-detail-options">
            <label className="export-full-history-toggle">
              <input
                type="checkbox"
                checked={includeFullResults}
                onChange={(e) => setIncludeFullResults(e.target.checked)}
                disabled={isExporting}
              />
              <span>Include full platform result JSON</span>
            </label>
            <small className="export-date-help">
              Every row already includes per-campaign counts for audiences, geos,
              private deals (PMP), keywords and creatives. Check this to also embed
              the complete DV360 / TTD / creative API responses — much larger files.
            </small>
          </div>

          <div className="export-card-actions">
            <button
              className={`export-button ${isExporting ? 'exporting' : ''}`}
              onClick={handleDataExport}
              disabled={
                isExporting ||
                accountsLoading ||
                !selectedAccountId ||
                (!fullHistory && (!startDate || !endDate))
              }
            >
              {isExporting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="button-icon" />
                  Exporting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} className="button-icon" />
                  {selectedAccountName ? `Export ${selectedAccountName} Data` : 'Export Data'}
                </>
              )}
            </button>
          </div>

          {exportStatus && (
            <div className="export-status success">
              {exportStatus}
            </div>
          )}

          {error && (
            <div className="export-status error">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataExport;
